import React, { useMemo } from "react";
import { connect } from "react-redux";
import { store } from "../../../store/configureStore";
import { scroll } from "../../../atlas-utils";
import SkeletonLoader from "../../../components/Periscope/SkeletonLoader";
import { ActionTypes } from "../../../actions/_types";
import ReactTable from "../../../components/Periscope/NewTable/ReactTable";
import EntityCard from "../../../components/Periscope/NewTable/EntityCard";
import EntityCell from "../../../components/Periscope/NewTable/EntityCell";
import EntityHead from "../../../components/Periscope/NewTable/EntityHead";
import { getPlatformDetailViaName } from "../../../helpers/periscope";
import { CATALOGUE_PLATFORMS_LOGO } from "../../../client-config";
import DangerIcon from "../../../components/_icons/DangerIcon";

const SpecificItemIssueTable = ({
	defaultPlatforms,
	periscopeSpecificItemIssueState,
	periscopeSpecificItemIssueData,
	fetchData,
	configItems,
	tableRef
}) => {
	const data = periscopeSpecificItemIssueData?.data?.objects?.data;
	const loading = periscopeSpecificItemIssueData?.loading;
	const error = periscopeSpecificItemIssueData?.error;
	const offset = periscopeSpecificItemIssueState?.offset;
	const limit = periscopeSpecificItemIssueState?.limit;

	const appliedFilters = periscopeSpecificItemIssueState?.appliedFilters;
	const appliedPlatformNames = appliedFilters?.platform_names;

	const platformsNames = useMemo(() => {
		const isInvalid =
			!appliedPlatformNames ||
			appliedPlatformNames.length === 0 ||
			appliedPlatformNames[0] === "all" ||
			appliedPlatformNames[0] === "all" ||
			appliedPlatformNames.length === 0 ||
			appliedPlatformNames[0] === "all" ||
			appliedPlatformNames.length === 0 ||
			appliedPlatformNames[0] === "null" ||
			appliedPlatformNames.includes(null);

		return isInvalid ? defaultPlatforms : appliedPlatformNames;
	}, [appliedPlatformNames, defaultPlatforms]);

	let headers = useMemo(() => {
		return ["location", ...platformsNames];
	}, [platformsNames]);

	const showSortOptions = platformsNames.length > 1;
	const dispatchSortFunction = (sort = null) => {
		if (sort == null) {
			store.dispatch({
				type: ActionTypes.SPECIFIC_ITEM_ISSUE_STATE_FILTERS,
				payload: { sort: null }
			});
		} else {
			store.dispatch({
				type: ActionTypes.SPECIFIC_ITEM_ISSUE_STATE_FILTERS,
				payload: sort
			});
		}
	};

	const handleClickSort = (column) => {
		if (showSortOptions) {
			const { sort } = periscopeSpecificItemIssueState || {};
			let order = "";
			store.dispatch({
				type: ActionTypes.SPECIFIC_ITEM_ISSUE_STATE_FILTERS,
				payload: { offset: 0 }
			});
			if (sort?.field === column) {
				order = sort?.order === "asc" ? "desc" : null;
			} else {
				order = "asc";
			}
			const newSort = order ? { sort: { field: column, order } } : null;
			dispatchSortFunction(newSort);
		}
	};

	const handlePagination = async (page) => {
		const offset = (page - 1) * limit;
		store.dispatch({
			type: ActionTypes.SPECIFIC_ITEM_ISSUE_STATE_FILTERS,
			payload: { offset: offset }
		});

		if (tableRef.current) {
			scroll({ top: tableRef.current.offsetTop - 57, left: 0 });
		}
	};

	const handlePageSize = async (field, size) => {
		store.dispatch({
			type: ActionTypes.SPECIFIC_ITEM_ISSUE_STATE_FILTERS,
			payload: { limit: size.value, offset: 0 }
		});
		if (tableRef.current) {
			scroll({ top: tableRef.current.offsetTop - 57, left: 0 });
		}
	};

	const dataCount = periscopeSpecificItemIssueData?.data?.objects?.meta?.totalCount;
	const bizPlatforms = useMemo(() => {
		return configItems?.bizPlatforms?.items;
	}, [configItems?.bizPlatforms?.items]);

	const duplicateValues = useMemo(() => {
		let valuesPattern = ["null", "null", "null", "undefined", "undefined", "undefined"];
		let indexInPattern = 0;
		return Array.from({ length: 9 }).map((_) => {
			let obj = {};
			[...headers].forEach((key) => {
				obj[key] = valuesPattern[indexInPattern];
			});
			indexInPattern = (indexInPattern + 1) % valuesPattern.length;
			return obj;
		});
	}, [headers, defaultPlatforms, platformsNames]);

	const columnsData = useMemo(() => {
		return headers.map((col) => {
			return {
				id: col,
				render: () => {
					return (
						<EntityHead
							title={col}
							{...(col !== "location" && {
								imgSrc:
									getPlatformDetailViaName(col, bizPlatforms)?.image || CATALOGUE_PLATFORMS_LOGO[col],
								sort: platformsNames && platformsNames.length > 1,
								handleSortClick: handleClickSort,
								sortOrder: periscopeSpecificItemIssueState.sort?.order,
								sortedField: periscopeSpecificItemIssueState.sort?.field
							})}
						/>
					);
				}
			};
		});
	}, [headers, bizPlatforms, periscopeSpecificItemIssueState.sort]);

	const newColumns = useMemo(() => {
		return Object.keys(
			loading || error ? duplicateValues?.[0] || {} : (data && data.length > 0 && data?.[0]) || {}
		).map((obj) => ({
			accessor: obj,
			enableRowSpan: false,

			Cell: (props) => {
				if (props.column.id == "location") {
					return (
						<EntityCard
							loading={loading}
							error={error}
							rating={props?.value?.rating}
							ratingText
							star
							title={props.value.location_name}
						/>
					);
				} else {
					const missingImageAndDescription = props?.value?.missing_image_and_description;
					const missingImage = props?.value?.missing_image;
					const missingDescription = props?.value?.missing_description;
					const stringValue = missingImageAndDescription
						? "Missing image and description"
						: missingImage
							? "Missing image"
							: missingDescription
								? "Missing description"
								: "partialOffline";

					return (
						<EntityCell
							hideTooltip
							loading={loading}
							error={error}
							styles={{ width: `${100 / (platformsNames.length || 1)}%` }}
							classes={stringValue !== "partialOffline" ? "false-state" : "empty-state"}
							customContent={stringValue !== "partialOffline"}
							value={JSON.stringify(props.value)}
						>
							<div className="custom-children">
								<DangerIcon /> {stringValue}
							</div>
						</EntityCell>
					);
				}
			}
		}));
	}, [data, loading, defaultPlatforms, platformsNames]);

	return (
		<div className="specific-item-issue-table-data ">
			{(configItems?.bizPlatforms?.items || []).length > 0 && platformsNames && platformsNames.length > 0 ? (
				<>
					<ReactTable
						classes="item-issues-selected-item-table"
						loading={loading}
						error={error}
						data={loading || error ? duplicateValues : data}
						columns={newColumns}
						colsArray={columnsData}
						thStyles={{
							cols: ["location"],
							styles: {
								width: `${100 / (platformsNames.length || 1)}%`,
								textAlign: "end"
							}
						}}
						limit={limit}
						offset={offset}
						goToPage={handlePagination}
						setPageSize={handlePageSize}
						count={dataCount}
						fetchData={fetchData}
					/>
				</>
			) : (
				""
			)}
		</div>
	);
};

const mapStateToProps = (state) => ({
	configItems: state.configItems,
	periscopeSpecificItemIssueState: state.periscopeSpecificItemIssueState,
	periscopeSpecificItemIssueData: state.periscopeSpecificItemIssueData
});
export default connect(mapStateToProps)(SpecificItemIssueTable);
